import { useEffect, useImperativeHandle, useMemo, RefObject } from "react";
import { useQueryClient, useMutation } from "react-query";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import debounce from "lodash.debounce";
import { Seller } from "@api/apiClient/sellers";
import { Alert, AlertType } from "@components/new/Alert";
import { LinkButton, LinkButtonSize } from "@components/new/LinkButton";
import TextInputContainer from "@containers/TextInputContainer";
import { validateAliKey, updateAliKey } from "@api/apiClient/sellers";
import { FormHandle } from "./types";
import styles from "./AliForm.module.scss";

export interface AliFormType {
  apiKey: string;
  newApiKey: string;
}

const AliForm = ({
  formRef,
  onStatusChange,
  onSuccess,
  onError,
  seller,
}: {
  formRef?: RefObject<FormHandle>;
  onStatusChange: (props: {
    disabled: boolean;
    isLoading?: boolean;
    title?: string;
  }) => void;
  onSuccess: () => void;
  onError: () => void;
  seller: Seller;
}) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();
  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<AliFormType>({
    defaultValues: {
      apiKey: seller.aliSeller?.token ? seller.aliSeller?.token.slice(0, 4) + "..." : "",
      newApiKey: "",
    },
    mode: "onBlur",
    reValidateMode: "onBlur",
  });

  const saveStoreMutation = useMutation({
    mutationFn: updateAliKey,
    onSuccess: () => onSuccess(),
    onError: () => onError(),
  });

  useEffect(() => {
    onStatusChange({
      disabled: !isValid,
      isLoading: Boolean(saveStoreMutation.isLoading),
      title: "",
    });
  }, [isValid, saveStoreMutation.isLoading]);

  const onSubmit = (form: AliFormType) => {
    saveStoreMutation.mutate({
      aliSellerId: seller.aliSeller?.id ?? "",
      newToken: form.newApiKey,
    });
  };

  const submit = handleSubmit(onSubmit);

  useImperativeHandle(formRef, () => ({
    submit,
  }));

  const validateApiKey = useMemo(
    () =>
      debounce(async (value, resolve) => {
        const result = await queryClient.fetchQuery({
          queryKey: ["validateAliKey", value, seller?.aliSeller?.id ?? ""],
          queryFn: () =>
            validateAliKey({ token: value, aliSellerId: seller?.aliSeller?.id }),
          staleTime: 5000,
        });

        resolve(
          result?.isSuccess ||
            // Сначала Ali-специфичный текст ошибки, затем общий, затем дефолтный
            t([
              `storesPage.addShopModal.errors.ali.${result.error}`,
              `storesPage.addShopModal.errors.${result.error}`,
              "defaultFormErrors.default",
            ]),
        );
      }, 500),
    [],
  );

  return (
    <form className={styles.form} onSubmit={submit}>
      <Alert
        type={AlertType.WARNING}
        title={t('storesPage.addShopModal.apikeyAlertTitle')}
        description={<LinkButton
          to={'https://unitrade-global.com/page139704916.html'}
          title={t('storesPage.addShopModal.apikeyAlertDescription')}
          size={LinkButtonSize.S}
          external
        />}
      />
      <TextInputContainer
        control={control}
        name="apiKey"
        title={t("storesPage.editShopModal.apiKeyInput")}
        disabled
        type="text"
        hasError={seller.aliSeller?.tokenStatus === "Invalid"}
        error={t(`storesPage.tokenStatus.${seller.aliSeller?.tokenStatus}`)}
      />
      <TextInputContainer
        control={control}
        name="newApiKey"
        title={t("storesPage.editShopModal.newApiKey")}
        placeholder={t("storesPage.editShopModal.newApiKeyPlaceholder")}
        rules={{
          required: t("defaultFormErrors.required"),
          validate: async (value) => {
            if (!value) return true;
            return new Promise((resolve) => {
              validateApiKey(value, resolve);
            });
          },
        }}
        maxLength={9999}
      />
    </form>
  );
};

export default AliForm;
